import { AppContext } from "../core/context";
import { productLibrary } from "../data";
import { CreateScreenshotUsingRenderTargetAsync } from "../engine";
import { Configuration, Entity } from "../types/type_def";
import { adjustCameraView } from "./camera";
import { isReady } from "./renderer";

export async function renderWithGUI(ctx: AppContext, successCallback: (data: any) => void) {

    const camera = ctx.topviewCamera;
    const width = 1500;
    const height = 1500;
    const antialiasing = true;
    let targetTextureSize = { width, height };

    if (!(height && width)) {
        BABYLON.Logger.Error("Invalid 'size' parameter !");
        return;
    }

    var save = ctx.engine.getAspectRatio;

    var scene = camera.getScene();
    var previousCamera = null;
    var previousCameras = scene.activeCameras;

    scene.activeCameras = null;

    if (scene.activeCamera !== camera) {
        previousCamera = scene.activeCamera;
        scene.activeCamera = camera;
    }

    ctx.engine.getAspectRatio = () => {
        return width / height;
    };

    ctx.GUI.scaleTo(width, height);      
    ctx.GUI.markAsDirty();
    ctx.GUI.invalidateRect(0, 0, width - 1, height - 1);

    scene.render(); // make sure the scene is ready to be rendered in the RTT with the right list of active meshes (which depends on the camera, that may have been changed above)

    // At this point size can be a number, or an object (according to engine.prototype.createRenderTargetTexture method)
    var texture = new BABYLON.RenderTargetTexture("screenShot", targetTextureSize, scene, false, false, BABYLON.Constants.TEXTURETYPE_UNSIGNED_INT, false, BABYLON.Texture.NEAREST_SAMPLINGMODE, undefined, true, undefined, undefined, undefined, 1);
    texture.renderList = null;
    texture.samples = 1;
    texture.renderSprites = true;

    texture.onBeforeRenderObservable.add(() => {
    });

    texture.onAfterRenderObservable.add(() => {
        ctx.engine.setDepthBuffer(false);
        ctx.GUI.layer.render();
        ctx.engine.setDepthBuffer(true);
        //advancedTexture._onResize();
    });

    ctx.engine.onEndFrameObservable.addOnce(() => {
        const prom = texture.readPixels(undefined, undefined, undefined, false)

        prom.then((data) => {
            BABYLON.Tools.DumpData(width, height, data, successCallback, "image/jpeg", undefined, true);
            texture.dispose();
        });
    });

    const renderToTexture = () => {
        // render the RTT
        scene.incrementRenderId();
        scene.resetCachedMaterial();
        texture.render(true);

        // re-render the scene after the camera has been reset to the original camera to avoid a flicker that could occur
        // if the camera used for the RTT rendering stays in effect for the next frame (and if that camera was different from the original camera)
        scene.incrementRenderId();
        scene.resetCachedMaterial();
        if (previousCamera) {
            scene.activeCamera = previousCamera;
        }
        scene.activeCameras = previousCameras;
        ctx.engine.getAspectRatio = save;
        camera.getProjectionMatrix(true); // Force cache refresh;
        scene.render();
    };

    if (antialiasing) {
        const fxaaPostProcess = new BABYLON.FxaaPostProcess('antialiasing', 1.0, scene.activeCamera);
        texture.addPostProcess(fxaaPostProcess);
        // Async Shader Compilation can lead to none ready effects in synchronous code
        if (!fxaaPostProcess.getEffect().isReady()) {
            fxaaPostProcess.getEffect().onCompiled = () => {
                renderToTexture();
            };
        }
        // The effect is ready we can render
        else {
            renderToTexture();
        }
    }
    else {
        // No need to wait for extra resources to be ready
        renderToTexture();
    }
}

export async function renderConfigurationThumbnail(ctx: AppContext, configuration: Configuration) {
    adjustCameraView(ctx, ctx.renderTargetCamera, ctx.currentModuleNode, ctx.scene, Math.PI/2.5, Math.PI/2.5, ctx.initialCameraRadius, ctx.defaultMinCameraRadius);
    let result = await BABYLON.Tools.CreateScreenshotUsingRenderTargetAsync(ctx.engine, ctx.renderTargetCamera, { width: 396, height: 222 }, "image/png", 2, true);               
    return result;
}

export async function renderConfigurationImage(ctx: AppContext, configuration: Configuration, filename: string, callback: (image: string) => void, resolutionMultiplier: number = 1, alpha: number = Math.PI / 2, beta: number = (Math.PI / 2) * 0.97, height?: number, minRadius?: number) {

    if (isReady(ctx)) {

        let heightRes = 768;            

        if (beta === 0) {
            heightRes = 1365;             
        }

        if (height) {
            ctx.renderTargetCamera.position.y = height;
            const dir = ctx.renderTargetCamera.position.subtract(ctx.renderTargetCamera.target);
            beta = Math.acos(dir.y / dir.length());             
        }        
  
        adjustCameraView(ctx, ctx.renderTargetCamera, ctx.currentModuleNode, ctx.scene, alpha, beta, undefined, minRadius);            

        CreateScreenshotUsingRenderTargetAsync(ctx.engine, ctx.renderTargetCamera, { width: 1365 * resolutionMultiplier, height: heightRes * resolutionMultiplier },"image/png", 4, true, (data) => {                
            data.then(result => {
                callback(result)
            })             
        }, filename, false, true, false, 1);          
     
    }
}

export function renderConfigurationImages(ctx: AppContext, configuration: Configuration, callback?: () => void) {

    renderConfigurationImage(ctx, configuration, ctx.currentConfiguration.series + "-front.png", (frontImage) => {
        renderConfigurationImage(ctx, configuration, ctx.currentConfiguration.series + "-side.png", (sideImage) => {
            renderConfigurationImage(ctx, configuration, ctx.currentConfiguration.series + "-back.png", (backImage) => {
                renderConfigurationImage(ctx, configuration, ctx.currentConfiguration.series + "-top.png", (topImage) => {
          
                if (callback){                        
                    callback();
                }

                }, 3, Math.PI * 0.5, 0, 0);
            }, 3, Math.PI * 1.5, Math.PI/2.5, 1.7, 4);
        }, 3, Math.PI * 0.25, Math.PI/2.5, 1.7,4);
    }, 3,  Math.PI * 0.5, Math.PI/2.5, 1.7,4);
}

export function renderDrawingWithGUI(ctx: AppContext, configuration: Configuration, callback: () => void) {

    adjustCameraView(ctx, ctx.topviewCamera, ctx.currentModuleNode, ctx.scene, undefined, undefined, undefined, ctx.defaultMinCameraRadius);    

    ctx.topviewCamera.beta = 0;
    ctx.topviewCamera.alpha = Math.PI / 2;
    ctx.topviewCamera.update();

    ctx.GUI.rootContainer.children.forEach(function (c) {
        if (c.name.indexOf("_label") >= 0) {
            c.dispose();                                      
        }
    });

    const entities = ctx.currentModule.entities.filter(function (entity: Entity) { return productLibrary.getProductCategory(entity.category) === "UPHOLSTRY" })

    for (let i = 0; i < entities.length; i++) {

        var entity = entities[i];
        var entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
        var centerNode = entityNode.getChildTransformNodes(true, function (t) { return t.id === "center" })[0];          

        var labelContainer = new BABYLON.GUI.Rectangle(ctx.currentModule.id + "_" + entity.id + "_label");
        labelContainer.width = "220px";
        labelContainer.height = "52px";
        labelContainer.cornerRadius = 4;
        labelContainer.color = "black";
        labelContainer.thickness = 1;
        labelContainer.background = "white";
        ctx.GUI.addControl(labelContainer);
        labelContainer.linkWithMesh(centerNode);
        labelContainer.linkOffsetY = -16;

        labelContainer.isVisible = false;

        var product = productLibrary.getProductByProductNo(entity.productNo);
        var productVariant = productLibrary.getProductVariantByConnectionType(product, ctx.moduleHelper.getEntityConnectionType(ctx.currentModule, entity));

        var text1 = new BABYLON.GUI.TextBlock(ctx.currentModule.id + "_" + entity.id + "_productNo");
        text1.text = productVariant.productNo.replace("/", "/\r\n") + "\r\n" + productVariant.axProductNo;
        text1.color = "black";
        text1.fontSize = 14;
        labelContainer.addControl(text1);
    }

    ctx.scene.executeWhenReady(() => {
        ctx.GUI.rootContainer.children.forEach(function (c) {
            if (c.name.indexOf("_label") >= 0) {
                c.isVisible = true;
            }
        });

        ctx.scene.render();

        renderWithGUI(ctx, (data) => {
            configuration.drawing = data;

            ctx.GUI.rootContainer.children.forEach(function (c) {
                if (c.name.indexOf("_label") >= 0) {
                    c.isVisible = false;                                            
                }
            });

            callback();
        });
    });
}
